/**
 * Spaces Planner Interactions
 *
 * Framework-agnostic math helpers for Pulse planner interactions.
 *
 * Primary responsibilities:
 * - Stack overlapping timed planner items into stable lanes
 * - Convert vertical drop offsets into snapped date/time slots
 * - Preserve timed item duration when moving tasks and events
 * - Format drop-preview labels for planner slots
 *
 * Dependencies:
 * - Native Date and Intl APIs
 */

// --------------

export type PulsePlannerTimedRange = {
  id: string
  start: Date
  end: Date | null
}

export type PulsePlannerStackedTimedItem<TItem> = {
  item: TItem
  id: string
  lane: number
  laneCount: number
  laneStartPercent: number
  laneWidthPercent: number
}

export type PulsePlannerTimeGrid = {
  startHour: number
  endHour: number
  hourRowHeight: number
  slotMinutes?: number
}

export type PulsePlannerDropSlotInput = {
  date: Date
  offsetY: number
  grid: PulsePlannerTimeGrid
  durationMinutes?: number
}

export type PulsePlannerDropSlot = {
  dateKey: string
  start: Date
  end: Date
  startMinutes: number
  endMinutes: number
  durationMinutes: number
}

export type PulsePlannerMoveTimedRangeInput = {
  originalStart: Date
  originalEnd: Date | null
  targetStart: Date
  fallbackDurationMinutes?: number
  minDurationMinutes?: number
}

export type PulsePlannerMovedTimedRange = {
  start: Date
  end: Date
  durationMinutes: number
}

export type PulsePlannerDropPreviewLabelInput = {
  start: Date
  end: Date
}

export type PulsePlannerDropPreviewLabelOptions = {
  locale?: string
  timeZone?: string
  includeDate?: boolean
}

type NormalizedTimedItem<TItem> = {
  item: TItem
  id: string
  originalIndex: number
  startMs: number
  endMs: number
}

type TimedLaneAssignment<TItem> = {
  item: TItem
  id: string
  lane: number
  groupIndex: number
  originalIndex: number
}

const DEFAULT_SLOT_MINUTES = 15
const DEFAULT_DURATION_MINUTES = 60

/**
 * Default block length when drag-scheduling an undated item onto the
 * time grid — the web analog of mobile's PlannerDragSchedule. Keep in
 * sync with the schedule sheet and other planner defaults (60 min).
 */
export const PLANNER_DRAG_SCHEDULE_MINUTES = DEFAULT_DURATION_MINUTES
const DEFAULT_MIN_DURATION_MINUTES = 15
const MS_PER_MINUTE = 60 * 1000

// --------------

export function stackPulsePlannerTimedItems<TItem>(
  items: readonly TItem[],
  getRange: (item: TItem) => PulsePlannerTimedRange
): PulsePlannerStackedTimedItem<TItem>[] {
  const normalizedItems = normalizeTimedItems(items, getRange)
  const assignments: TimedLaneAssignment<TItem>[] = []
  let group: NormalizedTimedItem<TItem>[] = []
  let groupEndMs = Number.NEGATIVE_INFINITY

  for (const item of normalizedItems) {
    if (group.length > 0 && item.startMs >= groupEndMs) {
      assignments.push(...assignTimedLanes(group, assignments.length))
      group = []
      groupEndMs = Number.NEGATIVE_INFINITY
    }

    group.push(item)
    groupEndMs = Math.max(groupEndMs, item.endMs)
  }

  if (group.length > 0) {
    assignments.push(...assignTimedLanes(group, assignments.length))
  }

  return assignments
    .map((assignment) => {
      const groupAssignments = assignments.filter(
        (candidate) => candidate.groupIndex === assignment.groupIndex
      )
      const laneCount = Math.max(1, ...groupAssignments.map((candidate) => candidate.lane + 1))

      return {
        item: assignment.item,
        id: assignment.id,
        lane: assignment.lane,
        laneCount,
        laneStartPercent: (assignment.lane / laneCount) * 100,
        laneWidthPercent: 100 / laneCount
      }
    })
    .sort((left, right) => left.originalIndex - right.originalIndex)
}

export function getPulsePlannerDropSlot({
  date,
  durationMinutes = DEFAULT_DURATION_MINUTES,
  grid,
  offsetY
}: PulsePlannerDropSlotInput): PulsePlannerDropSlot {
  const slotMinutes = normalizePositiveNumber(grid.slotMinutes, DEFAULT_SLOT_MINUTES)
  const normalizedDuration = normalizePositiveNumber(durationMinutes, DEFAULT_DURATION_MINUTES)
  const hourRowHeight = normalizePositiveNumber(grid.hourRowHeight, 1)
  const gridStartMinutes = getGridStartMinutes(grid)
  const gridEndMinutes = Math.max(gridStartMinutes + slotMinutes, getGridEndMinutes(grid))
  const rawMinutes = gridStartMinutes + (Math.max(0, offsetY) / hourRowHeight) * 60
  const snappedMinutes = snapMinutes(rawMinutes, slotMinutes)
  const latestStartMinutes = Math.max(gridStartMinutes, gridEndMinutes - normalizedDuration)
  const startMinutes = clamp(snappedMinutes, gridStartMinutes, latestStartMinutes)
  const endMinutes = startMinutes + normalizedDuration
  const start = setDateMinutes(date, startMinutes)
  const end = setDateMinutes(date, endMinutes)

  return {
    dateKey: getPulsePlannerDateKey(start),
    start,
    end,
    startMinutes,
    endMinutes,
    durationMinutes: normalizedDuration
  }
}

export function movePulsePlannerTimedRange({
  fallbackDurationMinutes = DEFAULT_DURATION_MINUTES,
  minDurationMinutes = DEFAULT_MIN_DURATION_MINUTES,
  originalEnd,
  originalStart,
  targetStart
}: PulsePlannerMoveTimedRangeInput): PulsePlannerMovedTimedRange {
  const fallbackDuration = normalizePositiveNumber(
    fallbackDurationMinutes,
    DEFAULT_DURATION_MINUTES
  )
  const minDuration = normalizePositiveNumber(minDurationMinutes, DEFAULT_MIN_DURATION_MINUTES)
  const rawDuration = originalEnd
    ? Math.round((originalEnd.getTime() - originalStart.getTime()) / MS_PER_MINUTE)
    : fallbackDuration
  const durationMinutes = Math.max(minDuration, rawDuration > 0 ? rawDuration : fallbackDuration)
  const start = new Date(targetStart)
  const end = new Date(start.getTime() + durationMinutes * MS_PER_MINUTE)

  return {
    start,
    end,
    durationMinutes
  }
}

export function formatPulsePlannerDropPreviewLabel(
  input: PulsePlannerDropPreviewLabelInput,
  options: PulsePlannerDropPreviewLabelOptions = {}
): string {
  const includeDate = options.includeDate ?? true
  const dateLabel = includeDate
    ? new Intl.DateTimeFormat(options.locale, {
        day: 'numeric',
        month: 'short',
        timeZone: options.timeZone,
        weekday: 'short'
      }).format(input.start)
    : null
  const timeFormatter = new Intl.DateTimeFormat(options.locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timeZone
  })
  const timeLabel = `${timeFormatter.format(input.start)} - ${timeFormatter.format(input.end)}`

  return dateLabel ? `${dateLabel}, ${timeLabel}` : timeLabel
}

export function getPulsePlannerDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

// --------------

function normalizeTimedItems<TItem>(
  items: readonly TItem[],
  getRange: (item: TItem) => PulsePlannerTimedRange
) {
  return items
    .map((item, originalIndex): NormalizedTimedItem<TItem> | null => {
      const range = getRange(item)
      const startMs = range.start.getTime()

      if (!Number.isFinite(startMs)) {
        return null
      }

      const rawEndMs = range.end?.getTime()
      const endMs =
        typeof rawEndMs === 'number' && Number.isFinite(rawEndMs) && rawEndMs > startMs
          ? rawEndMs
          : startMs + DEFAULT_MIN_DURATION_MINUTES * MS_PER_MINUTE

      return {
        item,
        id: range.id,
        originalIndex,
        startMs,
        endMs
      }
    })
    .filter((item): item is NormalizedTimedItem<TItem> => item !== null)
    .sort((left, right) => {
      return (
        left.startMs - right.startMs || left.endMs - right.endMs || left.id.localeCompare(right.id)
      )
    })
}

function assignTimedLanes<TItem>(group: readonly NormalizedTimedItem<TItem>[], groupIndex: number) {
  const laneEndTimes: number[] = []

  return group.map((item): TimedLaneAssignment<TItem> => {
    const lane = laneEndTimes.findIndex((endMs) => endMs <= item.startMs)
    const assignedLane = lane === -1 ? laneEndTimes.length : lane
    laneEndTimes[assignedLane] = item.endMs

    return {
      item: item.item,
      id: item.id,
      lane: assignedLane,
      groupIndex,
      originalIndex: item.originalIndex
    }
  })
}

function getGridStartMinutes(grid: PulsePlannerTimeGrid) {
  return normalizeHour(grid.startHour) * 60
}

function getGridEndMinutes(grid: PulsePlannerTimeGrid) {
  return (normalizeHour(grid.endHour) + 1) * 60
}

function normalizeHour(hour: number) {
  return clamp(Math.floor(hour), 0, 23)
}

function normalizePositiveNumber(value: number | undefined, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback
}

function snapMinutes(minutes: number, slotMinutes: number) {
  return Math.round(minutes / slotMinutes) * slotMinutes
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function setDateMinutes(date: Date, minutes: number) {
  const nextDate = new Date(date)
  nextDate.setHours(0, minutes, 0, 0)
  return nextDate
}
